import { UI_CHROME_ART } from '../../content/assets/uiArt';
import { type UiSymbolName } from '../../content/assets/uiSymbols';
import { backgroundAssetPath } from '../../content/backgrounds/roomBackgrounds';
import { getReachableRoomIds } from '../../domain/dungeon/move';
import type { DungeonRoom, RoomStatus, RoomType } from '../../domain/game/types';
import { useGameStore } from '../../store/gameStore';
import { selectCurrentRoom, selectCurrentRoomId, selectDungeon, selectPlayer } from '../../store/selectors';
import { SvgIcon } from '../chrome/SvgIcon';

const ROOM_SYMBOLS: Record<RoomType, UiSymbolName> = {
  SAFE: 'room-safe',
  BOSS: 'room-boss',
  NORMAL_ENEMY: 'room-normal-enemy',
  ELITE_ENEMY: 'room-elite-enemy',
  TREASURE: 'room-treasure',
  NPC: 'room-npc',
  TRAP: 'room-trap',
  SECRET: 'room-secret',
  TELEPORTER: 'room-teleporter',
  EMPTY: 'room-empty',
};

function roomSymbol(status: RoomStatus, type: RoomType): UiSymbolName | null {
  if (status === 'UNEXPLORED') {
    return null;
  }
  return status === 'DISCOVERED' ? 'room-unknown' : ROOM_SYMBOLS[type];
}

function collectEdges(rooms: Record<string, DungeonRoom>) {
  const seen = new Set<string>();
  const edges: { from: DungeonRoom; to: DungeonRoom }[] = [];

  for (const room of Object.values(rooms)) {
    for (const connection of room.connections) {
      const target = rooms[connection.targetRoomId];
      if (
        !target ||
        room.status === 'UNEXPLORED' ||
        target.status === 'UNEXPLORED'
      ) {
        continue;
      }
      const key = [room.id, target.id].sort().join('::');
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      edges.push({ from: room, to: target });
    }
  }

  return edges;
}

export function DungeonMap() {
  const dungeon = useGameStore(selectDungeon);
  const currentRoomId = useGameStore(selectCurrentRoomId);
  const currentRoom = useGameStore(selectCurrentRoom);
  const player = useGameStore(selectPlayer);
  const moveToRoom = useGameStore((state) => state.moveToRoom);
  const rooms = Object.values(dungeon.rooms);
  const edges = collectEdges(dungeon.rooms);
  const reachableRoomIds = new Set(getReachableRoomIds(dungeon, player.alive));

  return (
    <section className="map-section">
      <header className="map-header">
        <div className="floor-title">
          <span>‹</span>
          <div>
            <small>TẦNG</small>
            <strong>{dungeon.floorNumber.toString().padStart(2, '0')}</strong>
          </div>
          <span>›</span>
        </div>
        <div className="map-compass" aria-label="La bàn, Bắc ở phía trên">
          <img src={UI_CHROME_ART.compass} alt="" aria-hidden="true" />
          <b>N</b><small>W</small><small>E</small><em>S</em>
        </div>
      </header>

      <div className="map-objective">
        <small>MỤC TIÊU TẦNG</small>
        <p><SvgIcon name="objective-marker" /> {dungeon.objectiveText}</p>
      </div>

      <div
        className="dungeon-map"
        style={{
          gridTemplateColumns: `repeat(${dungeon.gridWidth}, minmax(0, 1fr))`,
          gridTemplateRows: `repeat(${dungeon.gridHeight}, minmax(42px, 1fr))`,
        }}
        aria-label="Bản đồ tầng hiện tại"
      >
        <svg className="map-connections" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
          {edges.map((edge) => (
            <line
              key={`${edge.from.id}-${edge.to.id}`}
              x1={((edge.from.gridPos.x + 0.5) / dungeon.gridWidth) * 100}
              y1={((edge.from.gridPos.y + 0.5) / dungeon.gridHeight) * 100}
              x2={((edge.to.gridPos.x + 0.5) / dungeon.gridWidth) * 100}
              y2={((edge.to.gridPos.y + 0.5) / dungeon.gridHeight) * 100}
            />
          ))}
        </svg>

        {rooms.map((room) => {
          const current = room.id === currentRoomId;
          const hidden = room.status === 'UNEXPLORED';
          const reachable = reachableRoomIds.has(room.id);
          const clickable = !hidden && !current && reachable;
          const symbol = roomSymbol(room.status, room.type);

          return (
            <button
              key={room.id}
              className={[
                'map-room',
                `status-${room.status.toLowerCase()}`,
                `kind-${room.type.toLowerCase()}`,
                current ? 'current' : '',
                reachable ? 'is-reachable' : 'is-unreachable',
                room.isCleared ? 'cleared' : '',
              ].filter(Boolean).join(' ')}
              style={{
                gridColumn: room.gridPos.x + 1,
                gridRow: room.gridPos.y + 1,
              }}
              type="button"
              disabled={!clickable}
              aria-label={hidden
                ? 'Phòng chưa phát hiện'
                : current
                  ? `Đang ở ${room.name}`
                  : reachable
                    ? `Đi tới ${room.status === 'DISCOVERED' ? 'phòng chưa rõ' : room.name}`
                    : `${room.name} không nằm cạnh vị trí hiện tại`}
              aria-current={current ? 'location' : undefined}
              onClick={() => {
                if (clickable) {
                  moveToRoom(room.id);
                }
              }}
            >
              <span
                className="map-room-frame"
                style={{ WebkitMaskImage: `url(${UI_CHROME_ART.mapNodeFrame})`, maskImage: `url(${UI_CHROME_ART.mapNodeFrame})` }}
                aria-hidden="true"
              />
              {current ? (
                <span
                  className="map-current-marker"
                  style={{ WebkitMaskImage: `url(${UI_CHROME_ART.currentMarker})`, maskImage: `url(${UI_CHROME_ART.currentMarker})` }}
                  aria-hidden="true"
                />
              ) : null}
              {symbol ? <SvgIcon className="map-room-glyph" name={symbol} /> : null}
            </button>
          );
        })}
      </div>

      <div className="map-legend" aria-label="Chú thích bản đồ">
        <span><i className="legend-current" /> Vị trí</span>
        <span><i className="legend-safe" /> An toàn</span>
        <span><i className="legend-boss" /> Boss</span>
        <span><i className="legend-fog" /> Chưa rõ</span>
      </div>

      {currentRoom ? (
        <article className="current-room-card">
          <div>
            <small>PHÒNG HIỆN TẠI</small>
            <strong>{currentRoom.name}</strong>
            <span>{currentRoom.safetyTag}</span>
          </div>
          <div
            className="current-room-thumb"
            role="img"
            aria-label={`Ảnh minh họa ${currentRoom.name}`}
            style={{ backgroundImage: `url(${backgroundAssetPath(currentRoom.bgImageKey)})` }}
          />
        </article>
      ) : null}

      <p className="map-help">WASD / phím mũi tên · Chỉ di chuyển qua phòng liền kề</p>
    </section>
  );
}
